import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { aiModelRepository } from '../../../lib/repositories/ai-model.repository';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { thuLai } from '../../vai-tro/hooks/useVaiTro';
import type {
  AiModel,
  AiModelCreate,
  AiModelMultiplierSuggestionList,
  AiModelOption,
  AiModelUpdate,
  TenantAiModel,
} from '../types';

/**
 * Khoá cache. Danh mục nền tảng và trạng thái theo tenant cùng mở đầu `['ai-models']`: đổi danh mục
 * (tắt một model) làm bảng của mọi tenant tải lại bằng một khoá. Ô chọn model của module ocr ở dưới
 * `['ocr']` như mọi dữ liệu module đó.
 */
export const KHOA_MODEL_AI = {
  tatCa: ['ai-models'] as const,
  danhMuc: ['ai-models', 'danh-muc'] as const,
  theoTenant: (tenantId: string) => ['ai-models', 'tenant', tenantId] as const,
  luaChon: ['ocr', 'ai-models', 'lua-chon'] as const,
  goiYHeSo: ['ai-models', 'goi-y-he-so'] as const,
};

/** Cả danh mục nền tảng (vài chục dòng, không phân trang), lọc ở client. */
export function useAiModels() {
  return useQuery<AiModel[], ApiError>({
    queryKey: KHOA_MODEL_AI.danhMuc,
    queryFn: aiModelRepository.danhSach,
    staleTime: 60_000,
    retry: thuLai,
  });
}

/** Model tenant dùng được, cho ô chọn ở màn nhận dạng, cấu hình loại chứng từ, kho bản OCR (module ocr). */
export function useLuaChonModelAi() {
  return useQuery<AiModelOption[], ApiError>({
    queryKey: KHOA_MODEL_AI.luaChon,
    queryFn: aiModelRepository.luaChon,
    // Quản trị nền tảng bật/tắt model cho tenant thì ô chọn phải thấy ngay khi mở màn: không giữ bản cũ.
    staleTime: 0,
    refetchOnMount: 'always',
    retry: thuLai,
  });
}

function useLamMoi() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: KHOA_MODEL_AI.tatCa });
    // Chạy giả lập, hai cổng chung một trình duyệt: ô chọn model ở cổng tenant cũng đổi theo.
    void queryClient.invalidateQueries({ queryKey: KHOA_MODEL_AI.luaChon });
  };
}

/** Thêm, sửa từ hộp thoại. Lỗi ràng buộc để form tự hiện dưới ô, nên hook không bật toast lỗi. */
export function useSaveAiModel() {
  const lamMoi = useLamMoi();
  const toast = useToast();
  const { t } = useT();
  return useMutation<
    AiModel,
    ApiError,
    { id: string; du: AiModelUpdate } | { id?: undefined; du: AiModelCreate; khoaLap: string }
  >({
    mutationFn: (v) => ('khoaLap' in v ? aiModelRepository.tao(v.du, v.khoaLap) : aiModelRepository.sua(v.id, v.du)),
    onSuccess: (kq, { id }) => {
      lamMoi();
      toast.success(id ? t('aiModel.daCapNhat', { ten: kq.label }) : t('aiModel.daThem', { ten: kq.label, khoa: kq.key }));
    },
  });
}

/** Bật, tắt, đặt mặc định từ menu của dòng: không có form để gắn lỗi nên lỗi ra toast. */
export function useDoiNhanhAiModel() {
  const lamMoi = useLamMoi();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation<AiModel, ApiError, { model: AiModel; du: AiModelUpdate }>({
    mutationFn: ({ model, du }) => aiModelRepository.sua(model.id, du),
    onSuccess: (kq, { du }) => {
      lamMoi();
      toast.success(
        du.is_default
          ? t('aiModel.daDatMacDinh', { ten: kq.label })
          : t(kq.is_active ? 'aiModel.daBat' : 'aiModel.daTat', { ten: kq.label }),
      );
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}

/** Hệ số hoà vốn từ giá vốn của Bảng giá model (ADR-0123 mục 7); chỉ đọc khi hộp gợi ý mở. */
export function useGoiYHeSo(batDau: boolean) {
  return useQuery<AiModelMultiplierSuggestionList, ApiError>({
    queryKey: KHOA_MODEL_AI.goiYHeSo,
    queryFn: aiModelRepository.goiYHeSo,
    enabled: batDau,
    staleTime: 0,
    retry: thuLai,
  });
}

/** Model đang bật của danh mục và tenant này dùng được hay không (tab "Model AI" của chi tiết tenant). */
export function useAiModelCuaTenant(tenantId: string) {
  return useQuery<TenantAiModel[], ApiError>({
    queryKey: KHOA_MODEL_AI.theoTenant(tenantId),
    queryFn: () => aiModelRepository.theoTenant(tenantId),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** `PUT` cả tập model bị tắt. Kết quả ghi thẳng vào cache để bảng khớp server ngay. */
export function useLuuAiModelCuaTenant(tenantId: string, tenTenant: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<TenantAiModel[], ApiError, string[]>({
    mutationFn: (disabledKeys) => aiModelRepository.datTatChoTenant(tenantId, disabledKeys),
    onSuccess: (kq) => {
      queryClient.setQueryData(KHOA_MODEL_AI.theoTenant(tenantId), kq);
      void queryClient.invalidateQueries({ queryKey: KHOA_MODEL_AI.luaChon });
      toast.success(t('aiModel.tenantDaLuu', { ten: tenTenant }));
    },
  });
}
